import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import {
  Form,
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionBody,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import SweetAlert from "react-bootstrap-sweetalert";

import { createPortal } from "../../../utils/services/portalEvents/portalEventsCreate";
import {
  create as createConfig,
  edit as editConfig,
  getByContractId,
} from "../../../utils/services/portalEvents/portalEventsConfigContracts";
import { uploadImage, validateUUID } from "./helpers";

import { PortalConfigForm } from "./partials/PortalConfigForm";
import { PortalForm } from "./partials/PortalForm";

const CreatePortal = (props) => {
  const token = props.state.global_user_data?.data?.token;
  const contractId = props.state.global_user_data?.data?.theme[0]?.id;

  const [formData, setFormData] = useState({
    newRepository: "",
    subDomain: "",
    plusContractIdDev: "",
    plusContractIdProd: "",
    sentryDsn: "",
  });

  const [configState, setConfigState] = useState({
    imageLoginScreen: "",
    imageLoginScreenPreview: null,
    iconNavigationBar: "",
    iconNavigationBarPreview: null,
    titleNavigationBar: "",
    primaryColor: "#ffffff",
    secondaryColor: "#000000",
  });

  const [errors, setErrors] = useState({});
  const [configErrors, setConfigErrors] = useState({});
  const [typeRequest, setTypeRequest] = useState("post");
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [createdDomains, setCreatedDomains] = useState(null);
  const [alert, setAlert] = useState(false);
  const [openItem, setOpenItem] = useState(["portalForm"]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const toggleAccordion = (id) => {
    setOpenItem((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue =
      ["newRepository", "subDomain"].includes(name) && value
        ? value.toLowerCase()
        : value;

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (["plusContractIdDev", "plusContractIdProd"].includes(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: value && !validateUUID(value),
      }));
    }
  };

  const onImageChange = (event, setPreview, setFile) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
      event.target.value = "";
    }
  };

  const loadExistingConfig = useCallback(async () => {
    let isMounted = true;
    try {
      const res = await getByContractId(contractId);
      if (
        isMounted &&
        res?.message === "findByContract" &&
        res.findByContract
      ) {
        const data = res.findByContract || {};
        setTypeRequest("put");
        setConfigState({
          imageLoginScreenPreview: data.imageLoginScreen || null,
          iconNavigationBarPreview: data.iconNavigationBar || null,
          titleNavigationBar: data.titleNavigationBar || "",
          primaryColor: data.primaryColor || "#FFFFFF",
          secondaryColor: data.secondaryColor || "#FFFFFF",
          imageLoginScreen: "",
          iconNavigationBar: "",
        });
      }
    } catch (error) {
      console.error("Erro ao carregar configuração:", error);
      setAlert(
        <SweetAlert danger title="Erro" onConfirm={() => setAlert(false)}>
          Erro ao carregar configuração existente!
        </SweetAlert>
      );
    } finally {
      if (isMounted) setLoadingConfig(false);
    }
    return () => {
      isMounted = false;
    };
  }, [contractId]);

  useEffect(() => {
    loadExistingConfig();
  }, [loadExistingConfig]);

  const validateForm = () => {
    const portalErrors = {};
    if (typeRequest !== "put") {
      Object.entries(formData).forEach(([k, v]) => {
        if (!v || (typeof v === "string" && !v.trim())) portalErrors[k] = true;
      });
    }

    const cfgErrors = {};
    if (typeRequest === "post" && !configState.imageLoginScreen)
      cfgErrors.imageLoginScreen = "A imagem da tela de login é obrigatória";
    if (typeRequest === "post" && !configState.iconNavigationBar)
      cfgErrors.iconNavigationBar =
        "O ícone da barra de navegação é obrigatório";
    if (!configState.titleNavigationBar)
      cfgErrors.titleNavigationBar = "O título da barra é obrigatório";
    if (!configState.primaryColor)
      cfgErrors.primaryColor = "A cor primária é obrigatória";
    if (!configState.secondaryColor)
      cfgErrors.secondaryColor = "A cor secundária é obrigatória";

    setErrors(portalErrors);
    setConfigErrors(cfgErrors);

    return (
      Object.keys(portalErrors).length === 0 &&
      Object.keys(cfgErrors).length === 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return setAlert(
        <SweetAlert warning title="Atenção" onConfirm={() => setAlert(false)}>
          Preencha todos os campos obrigatórios!
        </SweetAlert>
      );
    }

    setLoading(true);
    try {
      const [imageLoginUrl, iconUrl] = await Promise.all([
        configState.imageLoginScreen instanceof File
          ? uploadImage(configState.imageLoginScreen, token)
          : Promise.resolve(configState.imageLoginScreenPreview),
        configState.iconNavigationBar instanceof File
          ? uploadImage(configState.iconNavigationBar, token)
          : Promise.resolve(configState.iconNavigationBarPreview),
      ]);

      const configPayload = {
        contractId: formData.plusContractIdProd || contractId,
        imageLoginScreen: imageLoginUrl,
        iconNavigationBar: iconUrl,
        titleNavigationBar: configState.titleNavigationBar,
        primaryColor: configState.primaryColor,
        secondaryColor: configState.secondaryColor,
      };

      if (typeRequest === "post") {
        await Promise.all([
          createConfig(configPayload, token),
          createPortal(formData),
        ]);
      } else {
        await editConfig(configPayload, contractId, token);
      }

      await loadExistingConfig();
      setIsModalOpen(false);

      if (formData.subDomain) {
        const devDomain = `https://${formData.subDomain}.inteegratec.com.br`;
        const prodDomain = `https://${formData.subDomain}.sistemasinteegra.com.br`;
        setCreatedDomains({ dev: devDomain, prod: prodDomain });
      }

      setAlert(
        <SweetAlert success title="Sucesso" onConfirm={() => setAlert(false)}>
          {typeRequest === "post"
            ? "Portal criado e configuração detectada com sucesso!"
            : "Configuração atualizada com sucesso!"}
        </SweetAlert>
      );
    } catch (err) {
      console.error(err);
      setAlert(
        <SweetAlert danger title="Erro" onConfirm={() => setAlert(false)}>
          {err?.response?.data?.message ||
            err?.message ||
            "Erro ao processar a criação do portal!"}
        </SweetAlert>
      );
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    const portalValid =
      typeRequest === "put" ||
      (Object.values(formData).every(
        (v) => v && (typeof v !== "string" || v.trim() !== "")
      ) &&
        Object.values(errors).every((e) => !e));

    const configValid =
      (configState.imageLoginScreen || configState.imageLoginScreenPreview) &&
      (configState.iconNavigationBar || configState.iconNavigationBarPreview) &&
      configState.titleNavigationBar.trim() !== "" &&
      configState.primaryColor &&
      configState.secondaryColor;

    return portalValid && configValid;
  };

  if (loadingConfig) {
    return (
      <div
        style={{
          display: "flex",
          height: "70vh",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
        <p style={{ marginTop: 10 }}>Carregando configuração...</p>
      </div>
    );
  }

  return (
    <>
      <Accordion open={openItem} toggle={toggleAccordion}>
        <AccordionItem>
          <AccordionHeader targetId="portalForm">
            Criação + Configuração do Portal
          </AccordionHeader>
          <AccordionBody accordionId="portalForm">
            <Form onSubmit={handleSubmit}>
              {typeRequest !== "put" && (
                <PortalForm
                  formData={formData}
                  errors={errors}
                  handleChange={handleChange}
                />
              )}

              {typeRequest !== "put" && (
                <p style={{ padding: 0, margin: 0 }}>
                  ⚠️ Crie uma pré configuração inicial antes de finalizar a
                  criação do portal.
                </p>
              )}

              <Button
                color="secondary"
                className="mt-3"
                onClick={() => setIsModalOpen(true)}
              >
                Abrir Configuração do Portal
              </Button>
              <Button
                disabled={!isFormValid() || loading}
                color="primary"
                type="submit"
                className="mt-3 ms-2"
              >
                {loading
                  ? "Processando..."
                  : typeRequest === "post"
                  ? "Criar Portal"
                  : "Atualizar Portal"}
              </Button>
            </Form>
          </AccordionBody>
        </AccordionItem>
      </Accordion>

      <Modal isOpen={isModalOpen} toggle={() => setIsModalOpen(!isModalOpen)}>
        <ModalHeader toggle={() => setIsModalOpen(!isModalOpen)}>
          Configuração do Portal
        </ModalHeader>
        <ModalBody>
          <PortalConfigForm
            {...configState}
            setImageLoginScreen={(file) =>
              setConfigState((prev) => ({ ...prev, imageLoginScreen: file }))
            }
            setImageLoginScreenPreview={(val) =>
              setConfigState((prev) => ({
                ...prev,
                imageLoginScreenPreview: val,
              }))
            }
            setIconNavigationBar={(file) =>
              setConfigState((prev) => ({ ...prev, iconNavigationBar: file }))
            }
            setIconNavigationBarPreview={(val) =>
              setConfigState((prev) => ({
                ...prev,
                iconNavigationBarPreview: val,
              }))
            }
            setTitleNavigationBar={(val) =>
              setConfigState((prev) => ({ ...prev, titleNavigationBar: val }))
            }
            setPrimaryColor={(val) =>
              setConfigState((prev) => ({ ...prev, primaryColor: val }))
            }
            setSecondaryColor={(val) =>
              setConfigState((prev) => ({ ...prev, secondaryColor: val }))
            }
            configErrors={configErrors}
            onImageChange={onImageChange}
          />
          <Button
            color="primary"
            className="mt-3"
            onClick={() => setIsModalOpen(false)}
          >
            Fechar
          </Button>
        </ModalBody>
      </Modal>

      {alert}
    </>
  );
};

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(CreatePortal);
